import React, {useMemo} from 'react';

import {BulkActionDownload} from 'src/components/bulk-tool';
import {UserLink} from 'src/components/links';
import {EMAIL_SETTINGS} from 'src/constants';

import {Dropdown} from '@pb/kit-component-dropdown';
import {Group, Item} from '@pb/kit-component-group';
import {Icon, Icons} from '@pb/kit-component-icon';
import Label from '@pb/kit-component-label';
import {pluralize} from '@pb/kit-formatters';


import type {BulkActionResult} from 'src/components/bulk-tool/typedef';
import type {ProcessedEmailItem} from 'src/openapi/admin/models/ProcessedEmailItem';


type Props = {
    toolTitle: string,
    fileName: string,
    fileHeaders: Record<string, string>,
    bulkResult: BulkActionResult<ProcessedEmailItem, void>,
};

export const ResultModalDescription = ({toolTitle, fileName, fileHeaders, bulkResult}: Props) => {
    const downloadMeta = useMemo(() => {
        if (!bulkResult.itemList?.length) {
            return '';
        }

        const record = bulkResult.itemList[0];
        const {emailType, teamMembers} = record;

        let meta = [
            `Email type:,${EMAIL_SETTINGS[emailType].label},,,`,
        ];

        if (teamMembers?.length) {
            meta = [
                ...meta,
                ...teamMembers.map((member, i) => `${!i ? 'Team Members:' : ''},${member.memberEmail},,,`),
            ];
        }
        meta.push(',,,,');

        return meta.join('\n');
    }, [bulkResult]);

    const description = useMemo(() => {
        if (!bulkResult.itemList?.length) {
            return <b>Sending results</b>;
        }

        const record = bulkResult.itemList[0];
        const {emailType, teamMembers} = record;

        return (
            <Group gap="xs" alignItems="center">
                <div>Results of sending</div>
                <b>{EMAIL_SETTINGS[emailType].label}</b>
                {teamMembers?.length ? (
                    <Dropdown
                        placement="right"
                        hoverEnabled={true}
                        anchor={({anchorRef, getAnchorProps}) => (
                            <span ref={anchorRef} {...getAnchorProps()}>
                                <span>with the <b>team</b> <Icon type={Icons.INFO}/></span>
                            </span>
                        )}>
                        <Item>
                            <Label>{pluralize('Selected Team Member[s]', teamMembers.length)}</Label>
                            <Group direction="v" gap="xs" alignItems="start">
                                {teamMembers.map(member => (
                                    <UserLink
                                        key={member.memberId}
                                        userId={member.memberId}
                                        caption={member.memberEmail}
                                        blank={true}
                                        place="Bulk Send Welcome Email Result"
                                    />
                                ))}
                            </Group>
                        </Item>
                    </Dropdown>
                ) : null}
            </Group>
        );
    }, [bulkResult]);

    return (
        <Group justify="space-between" alignItems="center">
            {description}
            <BulkActionDownload
                toolName={toolTitle}
                fileName={fileName}
                headers={fileHeaders}
                startTime={bulkResult.startTime}
                meta={downloadMeta}
                body={bulkResult.itemList?.map(item => ({
                    userId: item.userId.toString(),
                    userEmail: item.email,
                    actionStatus: item.actionStatus,
                    errors: item.errors?.join('. '),
                })) as Record<string, string>[]}
            />
        </Group>
    );
};
